import { motion, AnimatePresence } from 'motion/react';
import { Mic } from 'lucide-react';

import { usePrefersReducedMotion } from '@/lib/motion';
import { cn } from '@/lib/utils';

type MicState = 'idle' | 'listening' | 'processing';

interface VoiceMicButtonProps {
  state: MicState;
  onPress: () => void;
  disabled?: boolean;
}

// --- Wave ring delays for the 3 concentric rings ---
const WAVE_DELAYS = [0, 0.3, 0.6];

function WaveRings({ reduced }: { reduced: boolean }) {
  return (
    <>
      {WAVE_DELAYS.map((delay, i) => (
        <motion.span
          key={i}
          className="pointer-events-none absolute inset-0 rounded-full border-2 border-lime-400/40"
          initial={{ scale: 1, opacity: 0.6 }}
          animate={
            reduced
              ? { scale: 1, opacity: 0.3 }
              : {
                  scale: [1, 2],
                  opacity: [0.6, 0],
                }
          }
          transition={
            reduced
              ? undefined
              : {
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeOut',
                  delay,
                }
          }
        />
      ))}
    </>
  );
}

export function VoiceMicButton({ state, onPress, disabled = false }: VoiceMicButtonProps) {
  const reduced = usePrefersReducedMotion();
  const isListening = state === 'listening';
  const isProcessing = state === 'processing';
  const isIdle = state === 'idle';

  // Glow box-shadow values
  const glowIdle = '0 0 20px rgba(132,204,22,0.15)';
  const glowListening = '0 0 32px rgba(132,204,22,0.55), 0 0 60px rgba(132,204,22,0.25)';

  return (
    <div className="relative flex items-center justify-center">
      {/* Glow ring — behind the button */}
      <motion.span
        className="pointer-events-none absolute rounded-full"
        animate={{
          boxShadow: isListening ? glowListening : glowIdle,
          opacity: isProcessing ? 0 : 1,
        }}
        transition={{ duration: 0.35, ease: 'easeInOut' }}
        style={{
          inset: -8,
          borderRadius: 9999,
          // Idle: gentle infinite pulse via Framer Motion
        }}
      >
        {/* Idle pulsing lime glow ring */}
        {isIdle && !reduced && (
          <motion.span
            className="absolute inset-0 rounded-full"
            animate={{ boxShadow: [glowIdle, '0 0 32px rgba(132,204,22,0.30)', glowIdle] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
      </motion.span>

      {/* Wave rings — only when listening */}
      <AnimatePresence>
        {isListening && (
          <motion.span
            key="wave-rings"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <WaveRings reduced={reduced} />
          </motion.span>
        )}
      </AnimatePresence>

      {/* Main button */}
      <motion.button
        type="button"
        onClick={onPress}
        disabled={disabled || isProcessing}
        aria-label={isListening ? 'Arreter' : 'Parler'}
        aria-pressed={isListening}
        // Size transition: processing → size-12 (48px), else size-24 (96px)
        animate={{
          width: isProcessing ? 48 : isListening ? 105.6 : 96, // 96 * 1.1 = 105.6 for listening
          height: isProcessing ? 48 : isListening ? 105.6 : 96,
          scale: 1, // explicit reset; scale handled via width/height
        }}
        whileTap={isProcessing ? undefined : { scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 280, damping: 22 }}
        className={cn(
          // Base layout
          'relative flex items-center justify-center rounded-full',
          'border-[length:var(--border-brutal)] focus-visible:outline-none',
          'focus-visible:ring-2 focus-visible:ring-lime-500 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed',
          // Idle state
          isIdle && [
            'bg-white dark:bg-charcoal-900',
            'text-charcoal-950 dark:text-charcoal-200',
            'border-charcoal-950 dark:border-charcoal-600',
            'shadow-[4px_4px_0_#0D0D1A]',
          ],
          // Listening state
          isListening && [
            'bg-lime-500',
            'text-charcoal-950',
            'border-charcoal-950 dark:border-charcoal-600',
            'shadow-[4px_4px_0_#0D0D1A]',
          ],
          // Processing state
          isProcessing && [
            'bg-charcoal-100 dark:bg-charcoal-800',
            'text-charcoal-400',
            'border-charcoal-300 dark:border-charcoal-600',
            'shadow-none',
            'opacity-70',
          ],
        )}
      >
        {/* Idle float animation wrapper */}
        <motion.span
          className="flex items-center justify-center"
          animate={isIdle && !reduced ? { y: [0, -6, 0] } : { y: 0 }}
          transition={
            isIdle && !reduced
              ? { duration: 3.5, repeat: Infinity, ease: 'easeInOut' }
              : { duration: 0.2 }
          }
        >
          <Mic
            className={cn('transition-all duration-200', isProcessing ? 'size-5' : 'size-10')}
            strokeWidth={isListening ? 2.5 : 2}
          />
        </motion.span>
      </motion.button>
    </div>
  );
}
